import { useEffect, useState } from 'react';
import { Check, Copy, Link2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import { Label } from 'components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'components/ui/select';

import type { ReportView, ReportViewVisibility } from './api/reportViewTypes';

type ShareReportViewModalProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    view: ReportView | null;
    isSaving?: boolean;
    onShare: (visibility: ReportViewVisibility) => Promise<ReportView | void> | ReportView | void;
};

function buildShareUrl(view: ReportView): string {
    const url = new URL(window.location.href);
    url.searchParams.set('report', view.baseReportId);
    url.searchParams.set('view', String(view.eventReportViewId));
    return url.toString();
}

export function ShareReportViewModal({
    open,
    onOpenChange,
    view,
    isSaving = false,
    onShare,
}: ShareReportViewModalProps) {
    const [visibility, setVisibility] = useState<ReportViewVisibility>('private');
    const [copied, setCopied] = useState(false);
    const [sharedView, setSharedView] = useState<ReportView | null>(view);

    useEffect(() => {
        if (open && view) {
            setVisibility(view.visibility);
            setSharedView(view);
            setCopied(false);
        }
    }, [open, view]);

    if (!view) {
        return null;
    }

    const shareUrl = sharedView ? buildShareUrl(sharedView) : '';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="reports-light-overlay w-[calc(100%-2rem)] overflow-hidden sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Share view</DialogTitle>
                    <DialogDescription>
                        Choose who can open “{view.name}”. Non-owners get a copy if they edit it.
                    </DialogDescription>
                </DialogHeader>

                <div className="min-w-0 space-y-4 py-2">
                    <div className="min-w-0 space-y-2">
                        <Label>Visibility</Label>
                        <Select
                            value={visibility}
                            onValueChange={(value) => setVisibility(value as ReportViewVisibility)}
                        >
                            <SelectTrigger className="w-full min-w-0">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="reports-light-overlay">
                                <SelectItem value="private">Private — only you</SelectItem>
                                <SelectItem value="team">Team — everyone on this host</SelectItem>
                                <SelectItem value="link">Link — anyone with the URL</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {(visibility === 'link' || sharedView?.visibility === 'link') && shareUrl ? (
                        <div className="min-w-0 space-y-2">
                            <Label>Share link</Label>
                            <div className="flex min-w-0 gap-2">
                                <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                                    <Link2 className="h-3.5 w-3.5 shrink-0" />
                                    <span className="truncate">{shareUrl}</span>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    className="shrink-0"
                                    onClick={async () => {
                                        await navigator.clipboard.writeText(shareUrl);
                                        setCopied(true);
                                        window.setTimeout(() => setCopied(false), 1500);
                                    }}
                                >
                                    {copied ? (
                                        <Check className="h-4 w-4" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </div>
                    ) : null}
                </div>

                <DialogFooter className="min-w-0">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
                        Close
                    </Button>
                    <Button
                        disabled={isSaving || visibility === view.visibility}
                        onClick={async () => {
                            const result = await onShare(visibility);
                            if (result) {
                                setSharedView(result);
                            }
                        }}
                    >
                        {isSaving ? 'Saving...' : 'Update sharing'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
